import { useState } from "react";
import { useLocation } from "wouter";
import { VIEW_PATH, pathToViewMode } from "../../shared/types/view-routes";
import type { ViewMode } from "../../shared/types";
import {
  Sun,
  CalendarIcon,
  CheckSquare,
  Flame,
  ListIcon,
  BarChart3,
  ClockIcon,
  CalendarWeekIcon,
  LayoutGridIcon,
  MoreDots,
  GridIcon,
  BookIcon,
  KanbanIcon,
  WrenchIcon,
} from "../icons";

const primaryItems: Array<{
  readonly mode: ViewMode;
  readonly label: string;
  readonly icon: (props: { className?: string }) => React.ReactNode;
}> = [
  { mode: "today", label: "Aujourd'hui", icon: Sun },
  { mode: "calendar", label: "Calendrier", icon: CalendarIcon },
  { mode: "tasks", label: "Tâches", icon: CheckSquare },
  { mode: "habits", label: "Habitudes", icon: Flame },
];

const moreItems: Array<{
  readonly mode: ViewMode;
  readonly label: string;
  readonly icon: (props: { className?: string }) => React.ReactNode;
}> = [
  { mode: "kanban", label: "Kanban", icon: KanbanIcon },
  { mode: "eisenhower", label: "Eisenhower", icon: GridIcon },
  { mode: "journal", label: "Journal", icon: BookIcon },
  { mode: "agenda", label: "Agenda", icon: ListIcon },
  { mode: "stats", label: "Stats", icon: BarChart3 },
  { mode: "weekly", label: "Semaine", icon: CalendarWeekIcon },
  { mode: "schedule", label: "Grille", icon: LayoutGridIcon },
  { mode: "free-slots", label: "Créneaux", icon: ClockIcon },
];

const ADVANCED_MODES = new Set<ViewMode>(["kanban", "eisenhower", "journal"]);

interface BottomNavProps {
  readonly advancedMode: boolean;
  readonly hiddenSections: ReadonlySet<ViewMode>;
  readonly onAdmin?: () => void;
  readonly onDev?: () => void;
}

export function BottomNav({ advancedMode, hiddenSections, onAdmin, onDev }: BottomNavProps) {
  const [location, navigate] = useLocation();
  const active = pathToViewMode(location);
  const [showMore, setShowMore] = useState(false);
  const filteredMoreItems = moreItems.filter(
    (item) =>
      (advancedMode || !ADVANCED_MODES.has(item.mode)) &&
      !hiddenSections.has(item.mode),
  );
  const filteredPrimaryItems = primaryItems.filter(
    (item) => !hiddenSections.has(item.mode),
  );
  const moreModes = new Set<ViewMode>(filteredMoreItems.map((i) => i.mode));
  const isMoreActive = moreModes.has(active);

  return (
    <>
      {showMore && (
        <>
          <div
            className="fixed inset-0 z-30 md:hidden"
            onClick={() => setShowMore(false)}
          />
          <div className="fixed bottom-16 left-0 right-0 z-40 flex justify-center pb-2 md:hidden px-4">
            <div className="bg-surface border border-border rounded-2xl shadow-lg grid grid-cols-4 gap-1 p-2 w-full max-w-sm">
              {onAdmin && (
                <button
                  onClick={() => { onAdmin(); setShowMore(false); }}
                  className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl transition-colors touch-feedback text-violet-500 hover:bg-violet-500/10"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>
                  <span className="text-[10px] font-medium">Admin</span>
                </button>
              )}
              {import.meta.env.DEV && onDev && (
                <button
                  onClick={() => { onDev(); setShowMore(false); }}
                  className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl transition-colors touch-feedback text-amber-500 hover:bg-amber-500/10"
                >
                  <WrenchIcon className="w-4 h-4" />
                  <span className="text-[10px] font-medium">Dev</span>
                </button>
              )}
              {filteredMoreItems.map(({ mode, label, icon: Icon }) => (
                <button
                  key={mode}
                  onClick={() => {
                    navigate(VIEW_PATH[mode]);
                    setShowMore(false);
                  }}
                  className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl transition-colors touch-feedback ${
                    active === mode
                      ? "text-accent bg-accent/10"
                      : "text-ink-muted hover:text-ink hover:bg-surface-alt"
                  }`}
                >
                  <Icon />
                  <span className="text-[10px] font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <nav
        className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border z-20 flex justify-center items-end md:hidden"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) / 2)" }}
      >
        <div className="max-w-lg w-full flex justify-around items-end">
          {filteredPrimaryItems.map(({ mode, label, icon: Icon }) => (
            <button
              key={mode}
              onClick={() => {
                navigate(VIEW_PATH[mode]);
                setShowMore(false);
              }}
              className={`flex flex-col items-center gap-0.5 py-3 px-3 min-w-12 transition-colors touch-feedback ${
                active === mode ? "text-accent" : "text-ink-muted"
              }`}
            >
              <Icon />
              <span className="text-[10px] font-medium">{label}</span>
            </button>
          ))}
          <button
            onClick={() => setShowMore(!showMore)}
            className={`flex flex-col items-center gap-0.5 py-3 px-3 min-w-12 transition-colors touch-feedback ${
              isMoreActive ? "text-accent" : "text-ink-muted"
            }`}
          >
            <MoreDots />
            <span className="text-[10px] font-medium">Plus</span>
          </button>
        </div>
      </nav>
    </>
  );
}
